// Device-mode primitive — determines where the app's front door (/index route)
// sends the user: personal mode routes to the parent dashboard (/parent),
// kiosk mode routes to the kid session (/play). This is a per-device,
// per-browser-install setting (localStorage), NOT per-child or per-user.
//
// Default = "kiosk" to preserve existing behavior for all current users.
// A parent opts in to "personal" via Parent Settings → Device mode.
//
// MA-03: role (parent|teacher) is deferred to Milestone B; deviceMode is
// the only primitive needed for the parent/kiosk front-door fork.
//
// MA-550: getDeviceMode()/parseDeviceMode() below are DELIBERATELY
// permissive (garbage/absent → kiosk) and stay that way — every existing
// caller + test pins that default. getDeviceModeRaw() (below) is a SEPARATE,
// strict probe added for userRole.ts's front-door fork alone, which needs
// to see the unset state parseDeviceMode's default otherwise hides.
import { useSyncExternalStore } from "react";

const KEY = "pw_device_mode";
type DeviceMode = "personal" | "kiosk";

/** Pure parse — "personal" → personal; anything else (null, garbage, "kiosk") → kiosk. */
export function parseDeviceMode(raw: string | null | undefined): DeviceMode {
  return raw === "personal" ? "personal" : "kiosk";
}

export function getDeviceMode(): DeviceMode {
  if (typeof localStorage === "undefined") return "kiosk";
  return parseDeviceMode(localStorage.getItem(KEY));
}

export function setDeviceMode(mode: DeviceMode): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    /* private mode / quota — keep in-memory snapshot in sync anyway */
  }
  refresh();
}

/**
 * MA-550 — the strict, non-defaulting twin of getDeviceMode()/parseDeviceMode().
 * Every OTHER caller in this file (and every existing test pinning it) wants
 * the permissive "garbage/absent → kiosk" default, because kiosk IS the
 * documented safe default for a fresh install. But userRole.ts's front-door
 * fork (resolveFrontDoorDestination) needed to tell "this device was NEVER
 * told what it is" apart from "this device was explicitly armed/declared
 * kiosk" — parseDeviceMode's permissive collapse makes those two
 * indistinguishable, which was the actual root cause of MA-550 (a
 * first-time teacher's role choice was silently discarded because the
 * fork couldn't see past the kiosk default to the unset state underneath
 * it). Strict allowlist, like roleOverrideFromSearch's in userRole.ts: only
 * the two literal stored strings count as EXPLICIT; anything else (absent
 * key, cleared storage, a future garbage value) is null.
 */
export function getDeviceModeRaw(): DeviceMode | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(KEY);
  return raw === "personal" || raw === "kiosk" ? raw : null;
}

/**
 * MA-550/551 — restores "unset" (removes the key entirely, not just
 * setting it to some default value). The ONLY caller is kioskArming.ts's
 * disarmDevice, restoring a device that had NEVER had pw_device_mode
 * explicitly set before it was armed for a class (armDeviceForClass stashes
 * that fact under pw_device_mode_prearm — see its header). Routed through
 * this module's own refresh() rather than a bare localStorage.removeItem
 * from outside, for the same reason setDeviceMode always calls refresh():
 * useDeviceMode()'s cached snapshot (the Settings device-mode toggle) must
 * never go stale relative to what's actually in localStorage.
 */
export function clearDeviceMode(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* private mode / quota — keep in-memory snapshot in sync anyway */
  }
  refresh();
}

let snapshot: DeviceMode = getDeviceMode();
const listeners = new Set<() => void>();

function refresh(): void {
  snapshot = getDeviceMode();
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** React binding for the Settings toggle. */
export function useDeviceMode(): DeviceMode {
  return useSyncExternalStore(subscribe, () => snapshot, () => "kiosk" as DeviceMode);
}
